import * as React from 'react'
import * as classNames from 'classnames'
import { motion } from 'framer-motion'
import { Control, UseFormRegister, FieldErrors } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import { InputText } from '~/atoms/form/InputText'
import { InputDate } from '~/atoms/form/InputDate'
import { InputSelect } from '~/atoms/form/InputSelect'
import { InputCheckbox } from '~/atoms/form/InputCheckbox'
import { InputRadio } from '~/atoms/form/InputRadio'
import { makeZeroPaddingNumberArray } from '@/utils/arrayUtil'
import { calcAgeStringWithDateString } from '@/utils/dateFormat'

import * as Style from './style.css'

export interface IProps {
  index: number
  minDate?: Date
  validation?: boolean
  animation?: boolean
  supportEvent: ISupportEventTakeCare
  supportChildren: IChildSummary[]
  control: Control<ICompleteReportFormStateStep2>
  errors: FieldErrors<ICompleteReportFormStateStep2>
  register: UseFormRegister<ICompleteReportFormStateStep2>
  deleteHandler: (index: number) => void
  changeHandoverPersonHandler: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void
  changeHandoverLocationHandler: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void
}

const INIT_ANIMATION_SETTING = {
  initial: { background: '#FDEEF5' },
  animate: { background: '#FFF' },
  transition: { duration: 3 },
}

const NONE_ANIMATION_SETTING = {
  initial: { background: '#FFF' },
  animate: { background: '#FFF' },
}

const HOUR_RANGE = makeZeroPaddingNumberArray(0, 23, 2)
const MINUTE_RANGE = makeZeroPaddingNumberArray(0, 59, 2)

export const SupportEventTakeCare = ({
  index,
  minDate,
  validation = true,
  animation = false,
  supportEvent,
  supportChildren,
  control,
  errors,
  register,
  deleteHandler,
  changeHandoverPersonHandler,
  changeHandoverLocationHandler,
}: IProps): JSX.Element => {
  const hasError = !!errors?.supportEvents?.[index]

  return (
    <motion.div
      {...(animation ? INIT_ANIMATION_SETTING : NONE_ANIMATION_SETTING)}
    >
      <div
        className={classNames({
          [Style.SupportEvent]: !hasError,
          [Style.SupportEventError]: hasError,
        })}
      >
        <button
          type="button"
          onClick={() => deleteHandler(index)}
          className={Style.SupportEventDelete}
        >
          <FontAwesomeIcon icon={faXmark} />
          削除
        </button>
        <div>
          <p className={Style.SupportEventName}>お預かり</p>
          <ul className={Style.SupportEventDescription}>
            <li>・お子様の保育開始時間を入力してください。</li>
            <li>
              ・1回のご予約で複数人のお子様をサポートし、かつ引き継ぎが個別に発生する場合、ページ下部の「保育内容を追加する」ボタンを押してお子様毎にスケジュールを入力をしてください。
            </li>
          </ul>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            日時
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <div className={Style.SupportEventInput}>
            <div className={Style.SupportEventDate}>
              <div className={Style.SupportEventDateDay}>
                <InputDate
                  defaultDate={supportEvent.startDate}
                  minDate={minDate}
                  control={control}
                  {...register(`supportEvents.${index}.startDate`)}
                />
              </div>
              <InputSelect
                {...register(`supportEvents.${index}.startHour`, {
                  required: validation && '入力必須項目です',
                })}
                value={supportEvent.startHour}
                isError={!!errors?.supportEvents?.[index]?.startHour}
              >
                {HOUR_RANGE.map((hour) => (
                  <option
                    key={`supportEvents.${index}.startHour.${hour}`}
                    value={hour}
                  >
                    {hour}
                  </option>
                ))}
              </InputSelect>
              <span className={Style.SupportEventDateSeparator}>：</span>
              <InputSelect
                {...register(`supportEvents.${index}.startMinute`, {
                  required: validation && '入力必須項目です',
                })}
                value={supportEvent.startMinute}
                isError={!!errors?.supportEvents?.[index]?.startMinute}
              >
                {MINUTE_RANGE.map((minute) => (
                  <option
                    key={`supportEvents.${index}.startMinute.${minute}`}
                    value={minute}
                  >
                    {minute}
                  </option>
                ))}
              </InputSelect>
            </div>
          </div>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            お子様
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <ul className={Style.SupportEventChild}>
            {supportChildren.map((child) => (
              <li key={`supportEvents.${index}.children.${child.id}`}>
                <InputCheckbox
                  label={`${child.last_name}${
                    child.first_name
                  }　${calcAgeStringWithDateString(child.birthday)}`}
                  value={child.id}
                  {...register(`supportEvents.${index}.children`, {
                    required: validation && '入力必須項目です',
                  })}
                />
              </li>
            ))}
          </ul>
          <div className={Style.SupportEventInputError}>
            <ErrorMessage
              errors={errors}
              name={`supportEvents.${index}.children`}
              render={({ message }) => <p>{message}</p>}
            />
          </div>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            引き継ぎ者
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <ul className={Style.SupportEventHandover}>
            <li>
              <InputRadio
                label="お母様"
                value="mother"
                {...register(`supportEvents.${index}.handoverPerson`, {
                  required: validation && '入力必須項目です',
                  onChange: changeHandoverPersonHandler,
                })}
              />
            </li>
            <li>
              <InputRadio
                label="お父様"
                value="father"
                {...register(`supportEvents.${index}.handoverPerson`, {
                  required: validation && '入力必須項目です',
                  onChange: changeHandoverPersonHandler,
                })}
              />
            </li>
            <li>
              <InputRadio
                label="おじい様"
                value="grandfather"
                {...register(`supportEvents.${index}.handoverPerson`, {
                  required: validation && '入力必須項目です',
                  onChange: changeHandoverPersonHandler,
                })}
              />
            </li>
            <li>
              <InputRadio
                label="おばあ様"
                value="grandmother"
                {...register(`supportEvents.${index}.handoverPerson`, {
                  required: validation && '入力必須項目です',
                  onChange: changeHandoverPersonHandler,
                })}
              />
            </li>
            <li>
              <InputRadio
                label="先生"
                value="teacher"
                {...register(`supportEvents.${index}.handoverPerson`, {
                  required: validation && '入力必須項目です',
                  onChange: changeHandoverPersonHandler,
                })}
              />
            </li>
            <li className={Style.SupportEventInputRadioWithText}>
              <span>
                <InputRadio
                  label="その他"
                  value="other"
                  {...register(`supportEvents.${index}.handoverPerson`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverPersonHandler,
                  })}
                />
              </span>
              <span>
                <InputText
                  disabled={supportEvent.handoverPerson !== 'other'}
                  {...register(`supportEvents.${index}.handoverPersonName`, {
                    required:
                      validation &&
                      supportEvent.handoverPerson === 'other' &&
                      '入力必須項目です',
                  })}
                />
              </span>
            </li>
          </ul>
          <div className={Style.SupportEventInputError}>
            <ErrorMessage
              errors={errors}
              name={`supportEvents.${index}.handoverPerson`}
              render={({ message }) => <p>{message}</p>}
            />
            <ErrorMessage
              errors={errors}
              name={`supportEvents.${index}.handoverPersonName`}
              render={({ message }) => <p>{message}</p>}
            />
          </div>
        </div>
        <div className={Style.SupportEventRow}>
          <p className={Style.SupportEventRowName}>
            引き継ぎ場所
            {validation && (
              <span className={Style.SupportEventRowNameRequired}>必須</span>
            )}
          </p>
          <div>
            <ul className={Style.SupportEventLocation}>
              <li>
                <InputRadio
                  label="自宅"
                  value="home"
                  {...register(`supportEvents.${index}.handoverLocation`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverLocationHandler,
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="学校"
                  value="school"
                  {...register(`supportEvents.${index}.handoverLocation`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverLocationHandler,
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="学童"
                  value="after_school_care"
                  {...register(`supportEvents.${index}.handoverLocation`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverLocationHandler,
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="幼稚園"
                  value="kindergarten"
                  {...register(`supportEvents.${index}.handoverLocation`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverLocationHandler,
                  })}
                />
              </li>
              <li>
                <InputRadio
                  label="保育園"
                  value="nursery"
                  {...register(`supportEvents.${index}.handoverLocation`, {
                    required: validation && '入力必須項目です',
                    onChange: changeHandoverLocationHandler,
                  })}
                />
              </li>
              <li className={Style.SupportEventInputRadioWithText}>
                <span>
                  <InputRadio
                    label="その他"
                    value="other"
                    {...register(`supportEvents.${index}.handoverLocation`, {
                      required: validation && '入力必須項目です',
                      onChange: changeHandoverLocationHandler,
                    })}
                  />
                </span>
                <span>
                  <InputText
                    disabled={supportEvent.handoverLocation !== 'other'}
                    {...register(
                      `supportEvents.${index}.handoverLocationName`,
                      {
                        required:
                          validation &&
                          supportEvent.handoverLocation === 'other' &&
                          '入力必須項目です',
                      },
                    )}
                  />
                </span>
              </li>
            </ul>
            <div className={Style.SupportEventInputError}>
              <ErrorMessage
                errors={errors}
                name={`supportEvents.${index}.handoverLocation`}
                render={({ message }) => <p>{message}</p>}
              />
              <ErrorMessage
                errors={errors}
                name={`supportEvents.${index}.handoverLocationName`}
                render={({ message }) => <p>{message}</p>}
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
